import { nextTick, onMounted, onUnmounted, reactive, Ref, watch } from 'vue';
import isNumber from 'lodash/isNumber';

const STARTUP_DELAY_MS = 3000;

interface Item {
  id: number;
  [key: string]: any;
}

export const useDelayedVisibilityMarking = (items: Ref<Item[]>, callback?: (itemId: number) => void) => {
  const itemRefs: Record<number, HTMLElement | null> = {};
  const readStates = reactive<Record<number, boolean>>({});

  let observer: IntersectionObserver | null = null;
  let delayTimeout: ReturnType<typeof setTimeout> | null = null;

  const startObserver = () => {
    if (observer) {
      return;
    }

    observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          const itemId = parseInt((entry.target as HTMLElement).dataset.id);
          if (entry.isIntersecting && isNumber(itemId)) {
            readStates[itemId] = true;
            callback?.(itemId);
            observer?.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '-60px 0px 0px 0px' }
    );

    items.value.forEach(item => {
      const el = itemRefs[item.id];
      if (el) {
        observer?.observe(el);
      }
    });
  };

  const clearDelay = () => {
    if (delayTimeout) {
      clearTimeout(delayTimeout);
      delayTimeout = null;
    }
    window.removeEventListener('scroll', handleOnScroll);
  };

  const handleOnScroll = () => {
    clearDelay();
    startObserver();
  };

  onMounted(() => {
    window.addEventListener('scroll', handleOnScroll, { passive: true });
    delayTimeout = setTimeout(() => {
      clearDelay();
      startObserver();
    }, STARTUP_DELAY_MS);
  });

  onUnmounted(() => {
    observer?.disconnect();
    clearDelay();
  });

  watch(
    items,
    async newItems => {
      await nextTick();
      newItems.forEach(item => {
        const el = itemRefs[item.id];
        if (el && !readStates[item.id]) {
          observer?.observe(el);
        }
      });
    },
    { deep: true, immediate: true }
  );

  return {
    itemRefs,
    readStates,
  };
};
